/** @format */

import axios from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";

// Using global axios to inherit auth headers from axiosInterceptor for mobile browser support

/**
 * Get all missed contributions for the authenticated user across all their unions
 */
export const submitGetMissedContributions = async () => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/auto-draft/missed-contributions`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Get missed contributions error:", error);
		throw error;
	}
};

/**
 * Check auto-draft eligibility for a specific union
 */
export const submitCheckAutoDraftEligibility = async (unionId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/auto-draft/eligibility/${unionId}`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Check auto-draft eligibility error:", error);
		throw error;
	}
};

// ==================== Admin Auto-Draft Service Methods ====================

/**
 * Get all users with auto-draft enabled (admin only)
 */
export const fetchAutoDraftUsersAsAdmin = async () => {
	try {
		const response = await axios.get(`${API_BASE_URL}/admin/auto-draft/users`, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get auto-draft users error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get auto-draft details for a specific user (admin only)
 */
export const fetchAutoDraftUserByIdAsAdmin = async (userId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/auto-draft/users/${userId}`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Get auto-draft user by id error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get auto-draft summary statistics (admin only)
 */
export const fetchAutoDraftSummaryAsAdmin = async () => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/auto-draft/summary`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Get auto-draft summary error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Cancel/disable auto-draft for a specific user (admin only)
 */
export const cancelAutoDraftUserAsAdmin = async (userId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/auto-draft/cancel/${userId}`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Cancel auto-draft user error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

// ==================== Admin Auto-Draft Results Service Methods ====================

export interface AutoDraftResultsParams {
	timezone?: string;
}

export interface AutoDraftResultsByDateParams {
	date: string;
	timezone?: string;
}

export interface AutoDraftFailedParams {
	startDate?: string;
	endDate?: string;
	page?: number;
	limit?: number;
	timezone?: string;
}

export interface AutoDraftRetryParams {
	page?: number;
	limit?: number;
}

/**
 * Get today's auto-draft results (admin only)
 */
export const fetchAutoDraftResultsAsAdmin = async (
	params?: AutoDraftResultsParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params?.timezone) queryParams.append("timezone", params.timezone);

		const url = `/admin/auto-draft/results${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
		const response = await axios.get(`${API_BASE_URL}${url}`, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get auto-draft results error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get auto-draft results for a specific date (admin only)
 */
export const fetchAutoDraftResultsByDateAsAdmin = async (
	params: AutoDraftResultsByDateParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params.timezone) queryParams.append("timezone", params.timezone);

		const url = `/admin/auto-draft/results/${params.date}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
		const response = await axios.get(`${API_BASE_URL}${url}`, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get auto-draft results by date error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get failed auto-draft attempts (admin only)
 */
export const fetchAutoDraftFailedAsAdmin = async (
	params?: AutoDraftFailedParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params?.startDate) queryParams.append("startDate", params.startDate);
		if (params?.endDate) queryParams.append("endDate", params.endDate);
		if (params?.page) queryParams.append("page", params.page.toString());
		if (params?.limit) queryParams.append("limit", params.limit.toString());
		if (params?.timezone) queryParams.append("timezone", params.timezone);

		const url = `/admin/auto-draft/failed${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
		const response = await axios.get(`${API_BASE_URL}${url}`, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get auto-draft failed attempts error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get auto-draft retry/pending attempts (admin only)
 */
export const fetchAutoDraftRetriesAsAdmin = async (
	params?: AutoDraftRetryParams,
) => {
	try {
		const queryParams = new URLSearchParams();
		if (params?.page) queryParams.append("page", params.page.toString());
		if (params?.limit) queryParams.append("limit", params.limit.toString());

		const url = `/admin/auto-draft/retries${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
		const response = await axios.get(`${API_BASE_URL}${url}`, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		console.error("Get auto-draft retries error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};

/**
 * Get details of a specific auto-draft attempt (admin only)
 */
export const fetchAutoDraftAttemptByIdAsAdmin = async (attemptId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/auto-draft/attempt/${attemptId}`,
			{
				withCredentials: true,
			},
		);
		return response.data;
	} catch (error: any) {
		console.error("Get auto-draft attempt by id error:", error);
		if (error.response && error.response.data) {
			throw error.response.data;
		} else {
			throw new Error(
				error.message || "Unknown Error Occurred While Making Request",
			);
		}
	}
};
